'use client'

import { MouseEvent } from 'react'
import classNames from 'classnames'
import { Cell, Spacer, Text, Divider } from '@vinted/web-ui'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'

import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import {
  StatusStyle,
  ConversationLinkActions,
  ConversationMessage,
  StatusMessage as StatusMessageType,
} from '@marketplace-web/messaging/conversation-messaging-data'
import { clickEvent } from '@marketplace-web/messaging/tracking-data'

import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'

import MessageTime from '../../MessageTime'
import LinkAction from '../../ConversationLinkAction/LinkAction'
import { ConversationAction } from '../../ConversationActions'
import { getAnchorClassNameMatch } from '../../../utils/regex'

type OwnProps = {
  transactionId?: number
  message: StatusMessageType
  toggleSidebar?: () => void
}

type Props = OwnProps & ConversationMessage

const StatusMessage = ({ transactionId, message, createdTime }: Props) => {
  const isCesModalVisibilityFixEnabled = useFeatureSwitch('confirm_resolved_modal_fix')
  const { track } = useTracking()
  const breakpoints = useBreakpoint()
  const { conversation } = useConversationContext()

  const transactionStatus = conversation?.transaction?.status
  const shipmentId = conversation?.transaction?.shipment?.id
  const shipmentStatus = conversation?.transaction?.shipment?.status

  const isActiveStatus = message.style !== StatusStyle.Clear
  const isActionMessage = !!message.actions
  const isWarningActiveStatus = message.style === StatusStyle.Red

  const getLinkAction = (text: string | null) => {
    const availableActions = Object.values(ConversationLinkActions).join('|')

    return getAnchorClassNameMatch(text, availableActions)
  }

  const handleTrackingLinkClick = (event: MouseEvent) => {
    if (!(event.target instanceof HTMLAnchorElement)) return

    const targetDetails = {
      transaction_id: transactionId,
      url: event.target.href,
      transaction_status: transactionStatus,
      shipment_id: shipmentId,
      shipment_status: shipmentStatus,
    }

    const trackingEvent = clickEvent({
      screen: 'message_reply',
      target: 'hyperlink',
      targetDetails: JSON.stringify(targetDetails),
    })

    track(trackingEvent)
  }

  const getButtonTheme = () => {
    if (isWarningActiveStatus) return 'warning'

    return undefined
  }

  const getContentTheme = () => {
    if (!isActionMessage) return 'muted'

    return undefined
  }

  const getTitleTheme = () => {
    if (isWarningActiveStatus && isActionMessage) return 'warning'

    if (!isActionMessage) return 'muted'

    return undefined
  }

  const { title, subtitle, actions } = message

  const textTheme = getContentTheme()
  const titleTheme = getTitleTheme()

  const getSubtitle = () => {
    return subtitle ? <Text as="span" theme={textTheme} html text={subtitle} format /> : null
  }

  const renderActions = () => {
    if (!actions) return null

    return (
      actions?.length > 0 && (
        <>
          <Spacer size="large" />
          <div className={breakpoints.phones ? '' : 'u-flexbox'}>
            <SeparatedList
              separator={
                breakpoints.phones ? (
                  <Spacer />
                ) : (
                  <div>
                    <Spacer />
                  </div>
                )
              }
            >
              {actions.map((action, index) => (
                <div key={index} data-testid={`conversation-status-actions-${action.action}`}>
                  <ConversationAction
                    {...action}
                    size="medium"
                    theme={getButtonTheme()}
                    isCesModalVisibilityFixEnabled={isCesModalVisibilityFixEnabled}
                  />
                </div>
              ))}
            </SeparatedList>
          </div>
        </>
      )
    )
  }

  const renderContent = () => {
    return (
      <Cell>
        <Cell
          styling="tight"
          title={title ? <Text as="h2" type="title" theme={titleTheme} text={title} /> : null}
          body={
            <>
              {getSubtitle()}
              {renderActions()}
            </>
          }
          testId="conversation-message--status-message"
        />
      </Cell>
    )
  }

  const renderLinkAction = () => {
    const linkAction = getLinkAction(message.subtitle)

    if (!linkAction) return null

    return <LinkAction action={linkAction} />
  }

  const statusClass = classNames('conversation-message__status', {
    'u-sticky-top u-zindex-bump': isActiveStatus,
  })

  return (
    <>
      <MessageTime time={createdTime} />
      <div className={statusClass}>
        <Divider />
        <span role="none" onClick={handleTrackingLinkClick}>
          {renderContent()}
        </span>
        <Divider />
      </div>
      {renderLinkAction()}
    </>
  )
}

export default StatusMessage
